import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  getCookie,
  NotificationManager,
  setCookie,
  SiteConfigManager,
  StorageManager,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';

import {
  ADULT_COOKIE_NAME,
  ADULT_POPUP_STATE,
  millisecondsInDay,
  SIGN_IN_POPUP_STORAGE_KEY,
  SIGN_UP_POPUP_STORAGE_KEY,
  SITE_OPENING_DISABLED_POPUP_STORAGE_KEY,
} from 'common/constants';

export const getMultiPopupConfig = trigger => {
  const config = SiteConfigManager.getConfig('config.config.multiPopups', {});
  if (config.enabled && config.trigger === trigger) return config;
};

const isShowAdultPopup = () => {
  const enablePopup18 = SiteConfigManager.getConfig('config.config.general.enablePopup18');
  if (!enablePopup18) return ADULT_POPUP_STATE.GRANTED;
  const adultCookieValue = getCookie(ADULT_COOKIE_NAME);
  if (!adultCookieValue) return ADULT_POPUP_STATE.SELECTION;
  return +adultCookieValue;
};

export const hidePopupWithRouting = key => StorageManager.removeItem(key);

export const isRightMultiPopupLocation = (
  pathname = window.location.pathname,
  search = window.location.search,
) =>
  !(
    pathname.includes(staticRoutes.signup) ||
    pathname.includes(staticRoutes.signin) ||
    pathname.includes(staticRoutes.forgotPassword) ||
    pathname.includes(staticRoutes.resetPassword) ||
    search.includes('cmd=signin') ||
    search.includes('cmd=signup') ||
    search.includes('cmd=signup-delay') ||
    search.includes('cmd=forgotPassword') ||
    search.includes('cmd=resetPassword') ||
    search.includes('cmd=resetPasswordPhone') ||
    search.includes('cmd=emailVerification')
  );

const popupTypes = {
  ADULT: 'adultPopup',
  MULTI: 'multiPopup',
};

const showPopup = (type, config, { onShowed } = {}) => {
  NotificationManager.display({
    data: config,
    id: type,
    type,
  });

  onShowed?.();
};

export const displayMultiPopup = (multiPopupKey, storageKey) => {
  const config = getMultiPopupConfig(multiPopupKey);
  if (config) {
    StorageManager.setItem(storageKey, true);
    setTimeout(
      () => {
        if (isRightMultiPopupLocation()) {
          showPopup(popupTypes.MULTI, config, { onShowed: () => hidePopupWithRouting(storageKey) });
        }
      },
      (config.delay || 0) * 1000,
    );
  }
};

const MultiPopupManager = ({ showMultiPopup }) => {
  const [adultPopupState, setAdultPopupState] = useState(isShowAdultPopup());
  const { pathname, search } = useLocation();
  const multiPopupsConfig = useMemo(
    () => ({
      signIn: getMultiPopupConfig('sign_in'),
      signUp: getMultiPopupConfig('sign_up'),
      siteOpening: getMultiPopupConfig('site_opening'),
    }),
    [],
  );

  useEffect(() => {
    if (adultPopupState === ADULT_POPUP_STATE.SELECTION) {
      setTimeout(() => {
        showPopup(popupTypes.ADULT, {
          onError: () => setCookie(ADULT_COOKIE_NAME, `${ADULT_POPUP_STATE.FORBIDDEN}`),
          onSuccess: () => {
            setCookie(ADULT_COOKIE_NAME, `${ADULT_POPUP_STATE.GRANTED}`);
            setAdultPopupState(ADULT_POPUP_STATE.GRANTED);
          },
        });
      });
    } else if (adultPopupState === ADULT_POPUP_STATE.FORBIDDEN) {
      setTimeout(() => {
        showPopup(popupTypes.ADULT, { isForbidden: true });
      });
    } else if (isRightMultiPopupLocation(pathname, search)) {
      if (showMultiPopup && multiPopupsConfig.siteOpening) {
        setTimeout(
          () => {
            const now = new Date();
            const storagePopupConfig = StorageManager.getItem(
              SITE_OPENING_DISABLED_POPUP_STORAGE_KEY,
            );
            let isShowSiteOpeningPopup = !storagePopupConfig;
            if (!isShowSiteOpeningPopup) {
              try {
                isShowSiteOpeningPopup = now.getTime() > +JSON.parse(storagePopupConfig);
              } catch (_) {
                /* empty */
              }
            }

            if (isShowSiteOpeningPopup && isRightMultiPopupLocation()) {
              showPopup(popupTypes.MULTI, multiPopupsConfig.siteOpening, {
                onShowed: () =>
                  StorageManager.setItem(
                    SITE_OPENING_DISABLED_POPUP_STORAGE_KEY,
                    now.getTime() +
                      millisecondsInDay / (multiPopupsConfig.siteOpening.displayTimes || 1),
                  ),
              });
            }
          },
          (multiPopupsConfig.siteOpening.delay || 0) * 1000,
        );
      }

      if (multiPopupsConfig.signUp && StorageManager.getItem(SIGN_UP_POPUP_STORAGE_KEY)) {
        setTimeout(
          () => {
            if (StorageManager.getItem(SIGN_UP_POPUP_STORAGE_KEY) && isRightMultiPopupLocation()) {
              showPopup(popupTypes.MULTI, multiPopupsConfig.signUp, {
                onShowed: () => hidePopupWithRouting(SIGN_UP_POPUP_STORAGE_KEY),
              });
            }
          },
          (multiPopupsConfig.signUp.delay || 0) * 1000,
        );
      }

      if (multiPopupsConfig.signIn && StorageManager.getItem(SIGN_IN_POPUP_STORAGE_KEY)) {
        setTimeout(
          () => {
            if (StorageManager.getItem(SIGN_IN_POPUP_STORAGE_KEY) && isRightMultiPopupLocation()) {
              showPopup(popupTypes.MULTI, multiPopupsConfig.signIn, {
                onShowed: () => hidePopupWithRouting(SIGN_IN_POPUP_STORAGE_KEY),
              });
            }
          },
          (multiPopupsConfig.signIn.delay || 0) * 1000,
        );
      }
    }
  }, [adultPopupState, multiPopupsConfig, pathname, search, showMultiPopup]);

  return null;
};

MultiPopupManager.propTypes = {
  showMultiPopup: PropTypes.bool,
};

export default MultiPopupManager;
